import React, { useMemo } from 'react';
import type { UptimeCheck, TimeRange } from './types';

interface UptimeStatusBarProps {
  checks: UptimeCheck[];
  timeRange: TimeRange;
  rangeHours: number;
  checkIntervalMin: number;
}

const MAX_BUCKETS = 90;

function getBucketColor(upCount: number, total: number): string {
  if (total === 0) return 'bg-muted/50';
  const pct = (upCount / total) * 100;
  if (pct >= 80) return 'bg-emerald-500';
  if (pct >= 50) return 'bg-amber-400';
  return 'bg-red-500';
}

function formatBarDate(d: Date, range: TimeRange): string {
  if (range === '3h' || range === '24h') {
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface BucketTooltip {
  visible: boolean;
  x: number;
  y: number;
  label: string;
  status: string;
}

export const UptimeStatusBar: React.FC<UptimeStatusBarProps> = ({ checks, timeRange, rangeHours, checkIntervalMin }) => {
  const now = Date.now();
  const rangeStart = now - rangeHours * 3600000;

  const bucketCount = useMemo(() => {
    const checksPerHour = 60 / checkIntervalMin;
    const natural = Math.ceil(rangeHours * checksPerHour);
    return Math.min(natural, MAX_BUCKETS);
  }, [rangeHours, checkIntervalMin]);

  const bucketMs = (rangeHours * 3600000) / bucketCount;

  const [tooltip, setTooltip] = React.useState<BucketTooltip>({ visible: false, x: 0, y: 0, label: '', status: '' });

  const buckets = useMemo(() => {
    return Array.from({ length: bucketCount }, (_, i) => {
      const start = rangeStart + i * bucketMs;
      const end = start + bucketMs;
      const inRange = checks.filter(c => {
        const t = new Date(c.created_at).getTime();
        return t >= start && t < end;
      });
      const upCount = inRange.filter(c => c.status === 'up').length;
      return { upCount, total: inRange.length, start, end };
    });
  }, [checks, rangeStart, bucketMs, bucketCount]);

  const handleMouseEnter = (idx: number, e: React.MouseEvent) => {
    const b = buckets[idx];
    const pct = b.total === 0 ? null : Math.round((b.upCount / b.total) * 100);
    const timeLabel = new Date(b.start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const status = b.total === 0
      ? 'No data recorded'
      : pct! >= 80 ? `Up (${pct}%)`
      : pct! >= 50 ? `Degraded (${pct}%)`
      : `Down (${pct}%)`;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTooltip({ visible: true, x: rect.left + rect.width / 2, y: rect.top, label: timeLabel, status });
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <div className="flex items-center gap-0.5">
          {buckets.map((b, i) => (
            <div
              key={i}
              onMouseEnter={(e) => handleMouseEnter(i, e)}
              onMouseLeave={() => setTooltip(t => ({ ...t, visible: false }))}
              className={`flex-1 h-8 rounded-sm cursor-default transition-opacity hover:opacity-75 ${getBucketColor(b.upCount, b.total)}`}
            />
          ))}
        </div>

        {tooltip.visible && (
          <div
            className="fixed z-50 pointer-events-none"
            style={{ left: tooltip.x, top: tooltip.y, transform: 'translate(-50%, calc(-100% - 8px))' }}
          >
            <div className="rounded-lg border border-border bg-card shadow-xl px-2.5 py-1.5 text-xs whitespace-nowrap">
              <p className="font-semibold text-foreground">{tooltip.label}</p>
              <p className="text-muted-foreground">{tooltip.status}</p>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>{formatBarDate(new Date(rangeStart), timeRange)}</span>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-muted/60 border border-border" />
            <span>Unknown</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-red-500" />
            <span>Uptime 0–49%</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-amber-400" />
            <span>Uptime 50–79%</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-emerald-500" />
            <span>Uptime 80–100%</span>
          </div>
        </div>
        <span>{formatBarDate(new Date(now), timeRange)}</span>
      </div>
    </div>
  );
};
